import { createEffect, createMemo, Show } from "solid-js";
//
import { Table } from ":src/components/list";
import { EmptyTable } from "./empty-table";
import type { GradingData, AParticipant, SubmissionStatus } from "../_model";
import { solidstate } from ":setup/qstate";

export type Props = {
  participants: GradingData["participants"];
  filter: { search?: string; status?: SubmissionStatus };
  onItemClicked: (props: { item: AParticipant; itemIndex: () => number }) => void;
};

export default function ParticipantsTable(props: Props) {
  const $participants = createMemo(() => {
    const result = [];
    const participants = props.participants;
    if (participants == undefined || participants.length <= 0) {
      return undefined;
    }
    const filters = props.filter;
    if (filters == undefined) {
      return participants;
    }
    const { search, status } = filters;
    for (const index in participants) {
      const item = participants[index];
      const search_values = `${item.first_name},${item.last_name}`; // search keys
      let remove = false;
      if (search != undefined && !search_values.toLowerCase().includes(search)) {
        remove = true;
      } else if (status != undefined) {
        const done = item.filtration_id != undefined;
        if (status === "done" && !done) {
          remove = true;
        } else if (status === "pending" && done) {
          remove = true;
        }
      }
      if (!remove) {
        result.push(item);
      }
    }
    return result;
  });

  return (
    <Table
      class="text-black"
      _items={$participants()}
      _fallback={<EmptyTable msg="no participants to filter" />}
      _class={{
        desktop: {
          table: "px0.2",
          headersWrapper: "min-h-2rem bg-bg-alt font-semibold rounded-t-md items-center content-start mb-2",
          headersAndCells: "flex items-center justify-start text-0.75rem px-2 flex-1",
          rows: "py-1 mb-2",
        },
        phone: {
          wrapper: "gap-y-3 py-2 px-0.5",
          card: "items-center shadow-md py-3 px-2 gap-y-2 rounded-xl !justify-between text-0.74em",
          header: "w-100px items-center text-center ",
          cell: "flex justify-start items-center overflow-x-auto",
        },
      }}
      _containers={{
        items: ({ item, itemIndex, screen }) => {
          if (item.filtration_id != undefined) {
            return {
              class: "!ring-success ring-2 ring-solid rounded-xl ",
            };
          }
          return {
            class: "!ring-border-card ring-2 ring-solid rounded-xl",
          };
        },
      }}
      _overlays={{
        items: ({ screen, item, itemIndex }) => {
          return (
            <div
              aria-label="item_name"
              class="w-full h-full rounded-xl cursor-pointer"
              onclick={() => {
                props.onItemClicked?.({ item, itemIndex });
              }}
            />
          );
        },
      }}
      _headers={{
        ID: {
          key: "id",
          class: {
            desktop: {
              headerAndCell: "!w-10px !pr-1",
            },
          },
        },
        Name: {
          key: ({ item }) => (item.first_name ?? "") + (item.last_name ?? ""),
          class: {
            desktop: {
              header: "min-w-100px truncate",
            },
          },
        },
        Grade: {
          class: {
            desktop: {
              headerAndCell: "!min-w-150px truncate",
            },
          },
          key: ({ item }) => item?.scores?.total?.score ?? "Not Graded",
        },
        Status: {
          class: {
            phone: {
              cell: "h-40px",
            },
          },
          key: ({ item, screen }) => {
            const status = item.filtration_id == undefined ? "pending" : "done";
            return (
              <span
                class={
                  "capitalize rounded-xl text-0.8em h-fit px-2 py-1 " +
                  " " +
                  (status === "pending" ? "bg-warn-shade text-warn" : "bg-success-shade text-success")
                }
              >
                {status}
              </span>
            );
          },
        },
        "Filtration Time": {
          class: {
            desktop: {
              headerAndCell: "!min-w-200px truncate",
            },
          },
          key: ({ item }) => item?.date,
        },
      }}
    />
  );
}
